<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.plus-circle-solid"></svg-vue>
          <h1 class="content-title">
            {{ $t('Create product') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <router-link class="btn btn-app" to="/admin/products">
            {{ $t('Back') }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="content-card-body">
        <loading :status="loading"></loading>
        <form @submit.prevent="saveProduct">
          <div class="md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
            <div class="md:col-span-1">
              <h3 class="form-description-head">
                {{ $t('Product details') }}
              </h3>
              <p class="form-description-text">{{ $t('This information will be displayed publicly') }}.</p>
            </div>
            <div class="mt-5 md:mt-0 md:col-span-2">
              <div class="grid grid-cols-3 gap-6">
                <div class="col-span-3">
                  <label class="form-label" for="name">{{ $t('Name') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Name')" class="form-input" id="name" required v-model="product.name" />
                  </div>
                </div>
                <div class="col-span-3 md:flex md:space-x-2">
                  <div class="w-full">
                    <label class="form-label" for="category">{{ $t('Category') }}</label>
                    <div class="pt-1">
                      <select class="form-input bg-white" id="category" required v-model="product.category_id">
                        <option :value="null" disabled>
                          {{ $t('Select an option') }}
                        </option>
                        <option :key="index" :value="category.id" v-for="(category, index) in productCategories">
                          {{ category.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="w-full">
                    <label class="form-label" for="supplier">{{ $t('Supplier') }}</label>
                    <div class="pt-1">
                      <select class="form-input bg-white" id="supplier" v-model="product.supplier_id">
                        <option :value="null" disabled>
                          {{ $t('Select an option') }}
                        </option>
                        <option :key="index" :value="supplier.id" v-for="(supplier, index) in productSuppliers">
                          {{ supplier.first_name }} {{ supplier.last_name }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="description">{{ $t('Description') }}</label>
                  <div class="pt-1">
                    <textarea rows="3" :placeholder="$t('Description')" class="form-input" id="description" v-model="product.description"></textarea>
                  </div>
                </div>
              </div>
            </div>
            <div class="md:col-span-3">
              <div class="py-3">
                <div class="border-t border-gray-200"></div>
              </div>
            </div>
            <div class="md:col-span-1">
              <h3 class="form-description-head">
                {{ $t('Product controls') }}
              </h3>
              <p class="form-description-text">{{ $t('Product listing control and other things') }}.</p>
            </div>
            <div class="mt-5 md:mt-0 md:col-span-2">
              <div class="grid grid-cols-3 gap-6">
                <div class="col-span-3">
                  <label class="form-label" for="product_type">{{ $t('Product type') }}</label>
                  <select v-model="productType" class="form-input" id="product_type">
                    <option value="1">{{ $t('Standard Product') }}</option>
                    <option value="2">{{ $t('Single product on serial based') }}</option>
                    <option value="3">{{ $t('Single product with multiple serial based items') }}</option>
                    <option value="4">{{ $t('Single with variants') }}</option>
                  </select>
                </div>

                <template v-if="productType == 1">
                  <div class="col-span-3 md:flex md:space-x-2">
                    <div class="w-full">
                      <label class="form-label" for="cost">
                        {{ $t('Unit cost') }}
                      </label>
                      <div class="pt-1">
                        <input :placeholder="$t('Unit cost')" type="number" step="0.01" class="form-input" id="cost" v-model="product.cost" required />
                      </div>
                    </div>
                    <div class="w-full">
                      <label class="form-label" for="price">
                        {{ $t('Unit Price') }}
                      </label>
                      <div class="pt-1">
                        <input :placeholder="$t('Unit Price')" type="number" step="0.01" class="form-input" id="price" v-model="product.price" required />
                      </div>
                    </div>
                  </div>
                  <div class="col-span-3 md:flex md:space-x-2">
                    <div class="w-full">
                      <label class="form-label flex justify-between" for="upc"> {{ $t('Barcode') }} ({{ $t('optional') }}) </label>
                      <div class="pt-1">
                        <input :placeholder="$t('Universal product code')" class="form-input" id="upc" v-model="product.upc" />
                      </div>
                    </div>
                    <div class="w-full">
                      <label class="form-label flex justify-between" for="sku"> {{ $t('Stock keeping unit') }} {{ $t('SKU') }} ({{ $t('optional') }}) </label>
                      <div class="pt-1">
                        <input :placeholder="$t('Stock keeping unit')" class="form-input" id="sku" v-model="product.sku" />
                      </div>
                    </div>
                  </div>
                  <div class="col-span-3 md:flex md:space-x-2">
                    <div class="w-full">
                      <label class="form-label" for="quantity"> {{ $t('Quantity') }} </label>
                      <div class="pt-1">
                        <input :placeholder="$t('Quantity')" class="form-input" id="quantity" type="number" v-model="product.quantity" />
                      </div>
                    </div>
                    <div class="w-full">
                      <label class="form-label" for="warehouse">{{ $t('Warehouse') }}</label>
                      <div class="pt-1">
                        <select class="form-input bg-white" id="supplier" v-model="product.warehouse_id">
                          <option :value="null" disabled>
                            {{ $t('Select an option') }}
                          </option>
                          <option :key="index" :value="warehouse.id" v-for="(warehouse, index) in productWarehouses">
                            {{ warehouse.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-if="productType == 2">
                  <div class="col-span-3 md:flex md:space-x-2">
                    <div class="w-full">
                      <label class="form-label" for="cost">
                        {{ $t('Unit cost') }}
                      </label>
                      <div class="pt-1">
                        <input :placeholder="$t('Unit cost')" type="number" step="0.01" class="form-input" id="cost" v-model="product.cost" required />
                      </div>
                    </div>
                    <div class="w-full">
                      <label class="form-label" for="price">
                        {{ $t('Unit Price') }}
                      </label>
                      <div class="pt-1">
                        <input :placeholder="$t('Unit Price')" type="number" step="0.01" class="form-input" id="price" v-model="product.price" required />
                      </div>
                    </div>
                  </div>
                  <div class="col-span-3 md:flex md:space-x-2">
                    <div class="w-full">
                      <label class="form-label flex justify-between" for="upc"> {{ $t('Barcode') }} ({{ $t('optional') }}) </label>
                      <div class="pt-1">
                        <input :placeholder="$t('Barcode')" class="form-input" id="upc" v-model="product.upc" />
                      </div>
                    </div>
                    <div class="w-full">
                      <label class="form-label flex justify-between" for="sku"> {{ $t('Stock keeping unit') }} {{ $t('SKU') }} ({{ $t('optional') }}) </label>
                      <div class="pt-1">
                        <input :placeholder="$t('Stock keeping unit')" class="form-input" id="sku" v-model="product.sku" />
                      </div>
                    </div>
                  </div>
                  <div class="col-span-3 md:flex md:space-x-2">
                    <div class="w-full">
                      <label class="form-label" for="quantity"> {{ $t('Quantity') }} </label>
                      <div class="pt-1">
                        <input :placeholder="$t('Quantity')" class="form-input" id="quantity" type="number" v-model="product.quantity" />
                      </div>
                    </div>
                    <div class="w-full">
                      <label class="form-label" for="warehouse">{{ $t('Warehouse') }}</label>
                      <div class="pt-1">
                        <select class="form-input bg-white" id="supplier" v-model="product.warehouse_id">
                          <option :value="null" disabled>
                            {{ $t('Select an option') }}
                          </option>
                          <option :key="index" :value="warehouse.id" v-for="(warehouse, index) in productWarehouses">
                            {{ warehouse.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div class="col-span-3 md:flex md:space-x-2">
                    <div class="w-full">
                      <label class="form-label" for="serial_number">
                        {{ $t('Serial number') }}
                      </label>
                      <div class="pt-1">
                        <input :placeholder="$t('Serial number')" class="form-input" id="serial_number" required v-model="product.serial_number" />
                      </div>
                    </div>
                    <div class="w-full">
                      <label class="form-label" for="identity">
                        {{ $t('Identity') }}
                      </label>
                      <div class="pt-1">
                        <input :placeholder="$t('IMEI')" class="form-input" id="identity" required v-model="product.identity" />
                      </div>
                    </div>
                  </div>
                </template>

                <template v-if="productType == 3">
                  <div class="col-span-3">
                    <div class="col-span-3 md:flex md:space-x-2">
                      <div class="w-full">
                        <label class="form-label" for="cost">
                          {{ $t('Unit cost') }}
                        </label>
                        <div class="pt-1">
                          <input :placeholder="$t('Unit cost')" type="number" step="0.01" class="form-input" id="cost" v-model="product.cost" required />
                        </div>
                      </div>
                      <div class="w-full">
                        <label class="form-label" for="price">
                          {{ $t('Unit Price') }}
                        </label>
                        <div class="pt-1">
                          <input :placeholder="$t('Unit Price')" type="number" step="0.01" class="form-input" id="price" v-model="product.price" required />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-span-3 md:flex md:space-x-2">
                    <div class="w-full">
                      <label class="form-label" for="quantity"> {{ $t('Quantity') }} </label>
                      <div class="pt-1">
                        <input :placeholder="$t('Quantity')" class="form-input" id="quantity" type="number" v-model="product.quantity" />
                      </div>
                    </div>
                    <div class="w-full">
                      <label class="form-label" for="warehouse">{{ $t('Warehouse') }}</label>
                      <div class="pt-1">
                        <select class="form-input bg-white" id="supplier" v-model="product.warehouse_id">
                          <option :value="null" disabled>
                            {{ $t('Select an option') }}
                          </option>
                          <option :key="index" :value="warehouse.id" v-for="(warehouse, index) in productWarehouses">
                            {{ warehouse.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div class="bg-gray-200 p-2 rounded-md col-span-3" v-if="product.quantity > 0">
                    <div v-for="(item, index) in product.variants" class="md:flex md:space-x-2 mb-2">
                      <div class="w-full">
                        <label class="form-label" :for="`sn${index}`"> {{ index + 1 }} - {{ $t('Serial number') }} </label>
                        <div class="pt-1">
                          <input :placeholder="$t('Serial number')" class="form-input" :id="`sn${index}`" required v-model="item.serial_number" />
                        </div>
                      </div>
                      <div class="w-full">
                        <label class="form-label" :for="`id${index}`">
                          {{ $t('Identity') }}
                        </label>
                        <div class="pt-1">
                          <input :placeholder="$t('IMEI')" class="form-input" :id="`id${index}`" required  v-model="item.identity" />
                        </div>
                      </div>
                      <div class="w-full">
                        <label class="form-label flex justify-between" :for="`ups${index}`"> {{ $t('Barcode') }} ({{ $t('optional') }}) </label>
                        <div class="pt-1">
                          <input :placeholder="$t('Barcode')" :id="`ups${index}`" class="form-input" v-model="item.upc" />
                        </div>
                      </div>
                      <div class="w-full">
                        <label class="form-label flex justify-between" :for="`sku${index}`"> {{ $t('SKU') }} ({{ $t('optional') }}) </label>
                        <div class="pt-1">
                          <input :placeholder="$t('SKU')" class="form-input" :for="`sku${index}`" v-model="item.sku" />
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <span class="inline-flex">
              <router-link class="btn btn-app shadow-sm rounded-md mr-4" to="/admin/products">
                {{ $t('Cancel') }}
              </router-link>
              <button class="btn btn-app shadow-sm rounded-md" type="submit">
                {{ $t('Save') }}
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>
<script>
export default {
  name: 'new',
  metaInfo() {
    return {
      title: this.$i18n.t('Create Product'),
    };
  },

  data() {
    return {
      loading: true,
      productWarehouses: [],
      productCategories: [],
      productSuppliers: [],
      productType: 1,
      product: {
        saleable: null,
        name: null,
        low_qty_alert: 5,
        category_id: null,
        supplier_id: null,
        description: null,
        variants: [
          {
            title: null,
            cost: 0,
            price: 0,
            identity: null,
            serial_number: null,
          },
        ],
        status: true,
        tax_type: false,
        has_variants: false,
        serial_number: null,
        sku: null,
        upc: null,
        identity: null,
        barcode_symbology: 'CODE128',
        is_serial_based: false,
        price: 2,
        cost: 1,
        quantity: 1,
        warehouse_id: 1,
      },
    };
  },
  watch: {
    productType() {
      if (this.productType == 1) {
        this.product.has_variants = false;
        this.product.is_serial_based = false;
      }
      if (this.productType == 2) {
        this.product.is_serial_based = true;
        this.product.has_variants = false;
      }
      if (this.productType == 3) {
        this.product.has_variants = true;
        this.product.is_serial_based = true;
      }
      if (this.productType == 4) {
        this.product.has_variants = true;
        this.product.is_serial_based = false;
      }
    },

    'product.quantity'(val) {
      this.product.variants = [];
      Array.apply(null, { length: val }).forEach(() => {
        this.product.variants.push({
          title: null,
          sku: null,
          upc: null,
          identity: null,
          serial_number: null,
          cost: this.product.cost,
          price: this.product.price,
          is_serial_based: true,
        });
      });
    },
  },
  mounted() {
    this.getWerehouses();
    this.getProductCategories();
    this.getProductSuppliers();
    if (this.$route.query.model && this.$route.query.cost && this.$route.query.price) {
      this.productType = 2;
      this.product.name = this.$route.query.model;
      this.product.cost = this.$route.query.cost;
      this.product.price = this.$route.query.price;
      this.product.identity = this.$route.query.imei;
      this.product.serial_number = this.$route.query.serial_number;
      this.product.saleable = this.$route.query.uuid;
      this.product.is_serial_based = true;
    }
  },
  methods: {
    getWerehouses() {
      this.loading = true;
      axios
        .get(route('warehouses.list'))
        .then((response) => {
          this.productWarehouses = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    saveProduct() {
      this.loading = true;
      axios
        .post(route('products.store'), this.product)
        .then((response) => {
          this.loading = false;
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.$router.push(`/admin/products/${response.data.product}/edit`);
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getProductCategories() {
      this.loading = true;
      axios
        .get(route('product.categories'))
        .then((response) => {
          this.productCategories = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getProductSuppliers() {
      this.loading = true;
      axios
        .get(route('product.suppliers'))
        .then((response) => {
          this.productSuppliers = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
